'use client'

import Image from 'next/image'

import { EnvironmentBadge } from '@/components/environment-badge'
import { Button } from '@mantine/core'
import { signIn } from 'next-auth/react'
import fullLogo from 'public/logo_full_navy.png'
import { useSyncExternalStore } from 'react'

import styles from './signin.module.css'

interface SignInProps {
  // Overrides the default tagline / session-expired text (e.g. the 404 page
  // explains the missing page while still offering sign-in).
  message?: string
  // Where to land after signing in. Defaults to the current URL; the 404 page
  // sends users home instead of back to the missing page.
  callbackUrl?: string
}

export default function SignIn({ message, callbackUrl }: SignInProps) {
  // SSR/prerender-safe localStorage read: the server snapshot renders the
  // default tagline and the returning-user message swaps in on hydration.
  const hasLoggedIn = useSyncExternalStore(
    () => () => {},
    () => Boolean(localStorage.getItem('hasLoggedIn')),
    () => false
  )

  return (
    <div
      className={`relative flex h-screen flex-col items-center justify-center ${styles.signInBackground}`}
    >
      <EnvironmentBadge className="absolute top-4 right-4" />
      <div className="flex w-[440px] flex-col items-center text-center">
        <Image
          className="mb-4"
          src={fullLogo}
          alt="Celerity Enterprises"
          width={340}
          priority
        />
        {message ? (
          <p className="mb-4 font-semibold text-[var(--mantine-color-celerityBrand-9)]">
            {message}
          </p>
        ) : hasLoggedIn ? (
          <p className="mb-4 font-normal text-[var(--mantine-color-celerityBrand-9)]">
            Your session has expired. Please sign in again.
          </p>
        ) : (
          <p className="mb-4 font-semibold text-[var(--mantine-color-celerityBrand-9)]">
            Increase efficiency and integrate end-to-end operations on a secure
            financial platform built for your industry, by industry leaders.
          </p>
        )}
        <Button
          variant="filled"
          color="celerityBrand.9"
          fw={600}
          fz="sm"
          size="md"
          radius="sm"
          mt="sm"
          px={48}
          onClick={() =>
            signIn('cognito', callbackUrl ? { callbackUrl } : undefined)
          }
        >
          Sign In
        </Button>
      </div>
    </div>
  )
}
